import React, { useState, useEffect } from 'react';
import { AlertCircle, Clock, CheckCircle, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { questionnaireService } from '../services/questionnaireService';
import type { QuestionnaireAssignment } from '../types/questionnaires';

const TeamMemberTasks: React.FC = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState<QuestionnaireAssignment[]>([]);
  const [openTasks, setOpenTasks] = useState<QuestionnaireAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.email) {
      loadAssignments();
    }
  }, [user]);

  const loadAssignments = async () => {
    if (!user?.email) return;
    
    try {
      setIsLoading(true);
      const data = await questionnaireService.getAssignmentsForUser(user.email);
      setAssignments(data);
      
      // Filter open tasks (not submitted, before deadline)
      const now = new Date();
      const open = data.filter(a => 
        a.status !== 'submitted' && 
        new Date(a.deadline) > now
      );
      setOpenTasks(open);
      
    } catch (error) {
      console.error('Error loading assignments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDeadline = (deadline: Date) => {
    return new Date(deadline).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const daysUntilDeadline = (deadline: Date) => {
    return Math.ceil((new Date(deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  // If there are open tasks, show them prominently
  if (openTasks.length > 0) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8 rounded-r-lg">
          <div className="flex items-start">
            <AlertCircle className="w-8 h-8 text-yellow-600 mr-4 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-bold text-yellow-900 mb-2">
                Sie haben {openTasks.length} offene Aufgabe{openTasks.length > 1 ? 'n' : ''}
              </h2>
              <p className="text-yellow-800">
                Bitte füllen Sie die folgenden Fragebögen bis zur Deadline aus.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {openTasks.map(assignment => {
            const days = daysUntilDeadline(assignment.deadline);
            const isUrgent = days <= 3;
            
            return (
              <div
                key={assignment.id}
                className={`
                  bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer
                  border-l-4 ${isUrgent ? 'border-red-500' : 'border-purple-500'}
                `}
                onClick={() => window.location.href = `/questionnaire/${assignment.id}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <FileText className="w-5 h-5 text-purple-600 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          {assignment.questionnaireTitle}
                        </h3>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-600 mt-2">
                        <Clock className="w-4 h-4 mr-2" />
                        <span className={isUrgent ? 'text-red-600 font-semibold' : ''}>
                          Deadline: {formatDeadline(assignment.deadline)}
                          {days > 0 && ` (noch ${days} Tag${days !== 1 ? 'e' : ''})`}
                        </span>
                      </div>
                      
                      <div className="mt-3">
                        <span className={`
                          inline-block px-3 py-1 rounded-full text-xs font-medium
                          ${assignment.status === 'in_progress' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-800'
                          }
                        `}>
                          {assignment.status === 'in_progress' ? 'In Bearbeitung' : 'Offen'}
                        </span>
                        
                        <span className="ml-2 text-xs text-gray-500">
                          {assignment.questionnaireMode === 'quick' ? 'Schnellerfassung' : 'Vollständiger Fragebogen'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors">
                        Ausfüllen →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Otherwise show dashboard with all assignments
  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Meine Fragebögen</h1>
      
      {assignments.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Keine Fragebögen</h2>
          <p className="text-gray-600">
            Ihnen wurden noch keine Fragebögen zugewiesen.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {assignments.map(assignment => {
            const isPastDeadline = new Date() > new Date(assignment.deadline);
            const isSubmitted = assignment.status === 'submitted';
            
            return (
              <div
                key={assignment.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <FileText className="w-5 h-5 text-gray-600 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          {assignment.questionnaireTitle}
                        </h3>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-600 mt-2">
                        <Clock className="w-4 h-4 mr-2" />
                        <span>
                          Deadline: {formatDeadline(assignment.deadline)}
                        </span>
                      </div>
                      
                      <div className="mt-3">
                        {isSubmitted ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Abgeschickt am {assignment.submittedAt ? formatDeadline(assignment.submittedAt) : ''}
                          </span>
                        ) : isPastDeadline ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Deadline überschritten
                          </span>
                        ) : (
                          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {assignment.status === 'in_progress' ? 'In Bearbeitung' : 'Offen'}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      <button
                        onClick={() => window.location.href = `/questionnaire/${assignment.id}`}
                        className={`
                          px-4 py-2 rounded-lg transition-colors
                          ${isSubmitted || isPastDeadline
                            ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                          }
                        `}
                      >
                        {isSubmitted ? 'Ansehen' : isPastDeadline ? 'Ansehen' : 'Ausfüllen →'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeamMemberTasks;

